import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  DataTable,
  Flex,
  FormattedDateTime,
  Icon,
  Image,
  PlainButton,
  Text,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import { useDraggable, useDroppable } from '@dnd-kit/react';
import type { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import plur from 'plur';
import { useMemo } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useAnalytics } from '@/services/analytics';

import { type Asset } from '@/services/db/asset/entity';
import { isEmptyString } from '@/utils/string';

import { fileSize } from '../../utils';
import { useAssetActions } from '../hooks/use-asset-actions';
import { canDeleteAsset, getDeleteBlockedReason } from '../utils/asset-validation';
import { getCreatorName } from '../utils/get-creator-name';
import { isPdf } from '../utils/is-pdf';
import { FolderIcon } from './folder-icon';
import * as styles from './index.css';

type AssetListImageProps = { asset: Asset };

function AssetListImage({ asset }: AssetListImageProps) {
  if (asset.is_dir) {
    return <FolderIcon className={styles.folderIconList} />;
  }

  if (isPdf(asset)) {
    return (
      <Box className={styles.assetListIcon}>
        <Icon name="file-pdf" size="md" />
      </Box>
    );
  }

  return <Image loading="lazy" src={asset.path} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />;
}

type AssetListNameProps = {
  asset: Asset;
  onClick: () => void;
  isClickable?: boolean;
  unselectableReason?: string;
  isChecked?: boolean;
  isDraggingActive?: boolean;
};

function AssetListName({
  asset,
  onClick,
  isClickable = true,
  unselectableReason,
  isChecked,
  isDraggingActive,
}: AssetListNameProps) {
  const draggable = useDraggable({
    id: asset.id,
    disabled: unselectableReason != null,
  });

  const droppable = useDroppable({
    id: asset.id,
    disabled: !asset.is_dir,
  });

  const isDragging = draggable.isDragSource;

  const handleClick = () => {
    if (unselectableReason != null) return;
    onClick();
  };

  const shouldShowOpacity = isDragging === true || (isDraggingActive === true && isChecked === true);
  const style = {
    justifyContent: 'flex-start' as const,
    opacity: shouldShowOpacity ? 0.5 : 1,
  };

  return (
    <Tooltip isCustom content={unselectableReason ?? ''} isDisabled={unselectableReason == null}>
      {(context) => (
        <PlainButton
          {...context.reference.getReferenceProps()}
          ref={mergeRefs([draggable.ref, droppable.ref, context.reference.setReference])}
          onClick={handleClick}
          display="flex"
          alignItems="center"
          gap="100"
          w="full"
          className={clsx(
            unselectableReason != null ? styles.assetUnselectable : undefined,
            !isClickable && styles.assetNotClickable,
            droppable.isDropTarget === true && asset.is_dir && styles.assetGridFolderDropping,
          )}
          style={style}
        >
          <Box className={unselectableReason != null ? styles.assetUnselectableContent : undefined}>
            <AssetListImage asset={asset} />
          </Box>
          <Box className={unselectableReason != null ? styles.assetUnselectableContent : undefined}>
            <Truncated size="s" weight="medium">
              {asset.name}
            </Truncated>
          </Box>
        </PlainButton>
      )}
    </Tooltip>
  );
}

type AssetListDateProps = { asset: Asset };

function AssetListDate({ asset }: AssetListDateProps) {
  const creatorName = getCreatorName(asset);

  return (
    <Flex direction="column">
      <FormattedDateTime date={asset.created_at.getTime()} format="medium" size="s" />
      {!isEmptyString(creatorName) ? (
        <Text size="xs" color="subtle">
          by {creatorName}
        </Text>
      ) : null}
    </Flex>
  );
}

type AssetListSizeProps = { asset: Asset };

function AssetListSize({ asset }: AssetListSizeProps) {
  if (asset.is_dir) {
    const itemCount = asset.size ?? 0;
    return (
      <Text size="s">
        {itemCount} {plur('item', itemCount)}
      </Text>
    );
  }

  return <Text size="s">{fileSize(asset.size)}</Text>;
}

type AssetListActionsProps = { asset: Asset; onDelete?: (asset: Asset) => void };

function AssetListActions({ asset, onDelete }: AssetListActionsProps) {
  const { track } = useAnalytics();
  const { handleCopyUrl, handleDelete, handleOptimize } = useAssetActions({
    onDelete,
  });

  if (asset.is_dir) {
    return onDelete != null ? (
      <ButtonGroup groupVariant="spaced">
        <Tooltip isCustom content={getDeleteBlockedReason(asset) ?? 'Delete folder'}>
          {(context) => (
            <Button
              {...context.reference.getReferenceProps()}
              ref={context.reference.setReference}
              variant="subtle"
              size="sm"
              icon="delete-trash"
              isIconOnly
              isDanger
              aria-label="Delete folder"
              isDisabled={!canDeleteAsset(asset)}
              onClick={(event) => handleDelete(asset, event)}
            />
          )}
        </Tooltip>
      </ButtonGroup>
    ) : null;
  }

  return (
    <ButtonGroup groupVariant="spaced">
      <Tooltip isCustom content="Copy URL">
        {(context) => (
          <Button
            {...context.reference.getReferenceProps()}
            ref={context.reference.setReference}
            variant="subtle"
            size="sm"
            icon="link"
            isIconOnly
            aria-label="Copy URL"
            onClick={(event) => handleCopyUrl(asset, event)}
          />
        )}
      </Tooltip>
      <Tooltip isCustom content="Open in new tab">
        {(context) => (
          <Button
            {...context.reference.getReferenceProps()}
            ref={context.reference.setReference}
            variant="subtle"
            size="sm"
            icon="view-on"
            isIconOnly
            aria-label="Open in new tab"
            href={asset.path}
            isExternal
          />
        )}
      </Tooltip>
      {!isPdf(asset) ? (
        <Tooltip isCustom content="Optimize Image File">
          {(context) => (
            <Button
              {...context.reference.getReferenceProps()}
              ref={context.reference.setReference}
              variant="subtle"
              size="sm"
              icon="collapse"
              isIconOnly
              aria-label="Optimize Image File"
              onClick={(event) => {
                handleOptimize(asset, event);
                track('assetOptimizer', 'optimizeImageFile', {
                  'Builder Type': 'design-studio',
                });
              }}
            />
          )}
        </Tooltip>
      ) : null}
      {onDelete != null ? (
        <Tooltip isCustom content="Delete asset">
          {(context) => (
            <Button
              {...context.reference.getReferenceProps()}
              ref={context.reference.setReference}
              variant="subtle"
              size="sm"
              icon="delete-trash"
              isIconOnly
              isDanger
              aria-label="Delete asset"
              onClick={(event) => handleDelete(asset, event)}
            />
          )}
        </Tooltip>
      ) : null}
    </ButtonGroup>
  );
}

type AssetListProps = {
  assets: Asset[];
  onFolderClick: (asset: Asset) => void;
  onAssetClick?: (asset: Asset) => void;
  onDelete?: (asset: Asset) => void;
  checkedAssetIds?: Set<string>;
  onCheck?: (checked: boolean, assetId: string) => void;
  onSelectAll?: (assets: Asset[]) => void;
  onClearAll?: () => void;
  unselectableAssetReasons?: Record<string, string>;
  isDraggingActive?: boolean;
};

export function AssetList({
  assets,
  onFolderClick,
  onAssetClick,
  onDelete,
  checkedAssetIds = new Set(),
  onCheck,
  onSelectAll,
  onClearAll,
  unselectableAssetReasons,
  isDraggingActive,
}: AssetListProps) {
  const rowSelection = useMemo(() => {
    const selection: Record<string, boolean> = {};
    for (const id of checkedAssetIds) {
      selection[id] = true;
    }
    return selection;
  }, [checkedAssetIds]);

  const columns = useMemo<ColumnDef<Asset>[]>(() => {
    const selectedInView = assets.filter((asset) => checkedAssetIds.has(asset.id)).length;

    return [
      ...(onCheck != null
        ? [
            {
              id: 'select',
              header: () => (
                <Checkbox
                  name="select-all"
                  ariaLabel="Select all"
                  isChecked={selectedInView === assets.length && assets.length > 0}
                  isIndeterminate={selectedInView > 0 && selectedInView !== assets.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onSelectAll?.(assets);
                    } else {
                      onClearAll?.();
                    }
                  }}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                  }}
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  name={`select-${row.original.id}`}
                  ariaLabel={`Select ${row.original.name}`}
                  isChecked={checkedAssetIds.has(row.original.id)}
                  onCheckedChange={(checked) => {
                    onCheck(checked === true, row.original.id);
                  }}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                  }}
                />
              ),
              meta: { width: 60 },
              enableSorting: false,
            } as ColumnDef<Asset>,
          ]
        : []),
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => (
          <AssetListName
            asset={row.original}
            unselectableReason={unselectableAssetReasons?.[row.original.id]}
            isChecked={checkedAssetIds.has(row.original.id)}
            isDraggingActive={isDraggingActive}
            isClickable={row.original.is_dir || onAssetClick != null}
            onClick={() => {
              if (row.original.is_dir) {
                onFolderClick(row.original);
              } else {
                onAssetClick?.(row.original);
              }
            }}
          />
        ),
        meta: { width: '3fr' },
      },
      {
        id: 'date',
        header: 'Date',
        accessorKey: 'created_at',
        cell: ({ row }) => <AssetListDate asset={row.original} />,
        meta: { width: '1fr' },
      },
      {
        id: 'size',
        header: 'Size',
        accessorKey: 'size',
        cell: ({ row }) => <AssetListSize asset={row.original} />,
        meta: { width: '1fr' },
      },
      {
        id: 'actions',
        header: '',
        meta: { align: 'end', width: 160 },
        cell: ({ row }) => (
          <Box display="flex" justifyContent="flex-end" className={styles.assetGridActions}>
            <AssetListActions asset={row.original} onDelete={onDelete} />
          </Box>
        ),
      },
    ];
  }, [
    assets,
    onCheck,
    onFolderClick,
    onAssetClick,
    onDelete,
    onSelectAll,
    onClearAll,
    unselectableAssetReasons,
    isDraggingActive,
    checkedAssetIds,
  ]);

  return (
    <DataTable
      data={assets}
      columns={columns}
      caption="Asset library files and folders"
      rowSelection={rowSelection}
      getRowId={(row) => String(row.id)}
    />
  );
}
